import {
 Plus, Bot
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from 'shared/components/ui/dialog';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 useState
} from 'react';
import {
 useNavigate
} from 'react-router-dom';

// Define types for the props
interface AddTableFormProps {
    onAddTable: (tableName: string) => void; // Callback function when adding a table
    tables: { id: string }[]; // Array of tables, assuming each table has an `id`
    isLoading: boolean; // Loading state, to disable the button when true
}

export function AddTableForm({
 onAddTable, tables, isLoading
}: AddTableFormProps) {
    const [tableName, setTableName] = useState<string>(''); // State for table name
    const [open, setOpen] = useState<boolean>(false); // Dialog open/close state
    const [errorMessage, setErrorMessage] = useState<string>(''); // Error message
    const [selectedTab, setSelectedTab] = useState<'ai' | 'manual'>('ai'); // Selected tab, either 'ai' or 'manual'
    const navigate = useNavigate();

    // Function to handle navigation after table creation
    const handleTableNavigate = (pathname: string) => {
        setOpen(false);
        navigate(`./${pathname}`);
    };

    // Function to handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const newId = tableName.trim();
        if (tables.some(table => table.id === newId)) {
            setErrorMessage('Table name already exists. Please choose a different name.');
        } else {
            onAddTable(newId);
            setTableName('');
            setErrorMessage('');
            setOpen(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(openState) => {
                setOpen(openState);
                if (!openState) {
                    // reset on close
                    setErrorMessage('');
                    setTableName('');
                    setSelectedTab('manual');
                }
            }}
        >
            <DialogTrigger asChild>
                <Button
                    className="w-full justify-start gap-2"
                    onClick={() => setOpen(true)}
                    disabled={isLoading}
                >
                    <Plus className="h-4 w-4" />
                    Create Data Table
                </Button>
            </DialogTrigger>

            <DialogContent className="max-w-[400px] sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">
                        Create New Data Table
                    </DialogTitle>
                    <p className="mt-2 text-gray-600 text-base max-w-xl">
                        Hi there, I am here to help you. You can create data tables with AI, and we also provide you an option to create manually. Choose your best option.
                    </p>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex border-b border-gray-300 mb-6 mt-6">
                    <button
                        className={`flex-1 py-3 text-center text-lg ${
                            selectedTab === 'ai'
                                ? 'border-b-2 border-black font-semibold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setSelectedTab('ai')}
                        type="button"
                    >
                        AI Data Table
                    </button>
                    <button
                        className={`flex-1 py-3 text-center text-lg ${
                            selectedTab === 'manual'
                                ? 'border-b-2 border-black font-semibold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setSelectedTab('manual')}
                        type="button"
                    >
                        Manual
                    </button>
                </div>

                {/* Tab Content */}
                {selectedTab === 'manual' && (
                    <form onSubmit={handleSubmit}>
                        <div className="grid gap-6 py-6">
                            <div className="grid grid-cols-4 items-center gap-6">
                                <Label htmlFor="table-name" className="text-right text-lg font-medium">
                                    Table Name
                                </Label>
                                <Input
                                    id="table-name"
                                    value={tableName}
                                    onChange={(e) => setTableName(e.target.value)}
                                    className="col-span-3 text-lg"
                                    autoFocus
                                />
                            </div>

                            {errorMessage && (
                                <div className="text-sm text-red-600 mt-2">{errorMessage}</div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button type="submit" size="lg">
                                Create Table
                            </Button>
                        </DialogFooter>
                    </form>
                )}

                {selectedTab === 'ai' && (
                    <div className="py-10 text-center text-gray-700">
                        <div className="border rounded-lg p-8 max-w-lg mx-auto shadow-sm">
                            <div className="flex items-center justify-center mb-6">
                                <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                                    <Bot className="h-8 w-8 text-primary" />
                                </div>
                            </div>

                            <h2 className="text-2xl font-semibold mb-3">AI Assisted Data Table Creation</h2>
                            <p className="text-base text-gray-400 mb-8 max-w-md mx-auto">
                                Use natural language to quickly generate your database tables.
                            </p>

                            <Button
                                className="w-full"
                                size="lg"
                                onClick={() => handleTableNavigate('create-table-ai')}
                            >
                                Start AI Table Creation
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
